// src/services/reputationService.js
import { logger } from "../utils/logger";
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';

/**
 * Calculate reputation metrics for a helper based on real job data
 * @param {string} helperId - The helper's user ID
 * @returns {Promise<Object>} Reputation metrics
 */
export async function calculateHelperReputation(helperId) {
  try {
    logger.log('🔍 Calculating reputation for helper:', helperId);
    // Get all jobs where this helper is assigned
    let allJobs = [];
    try {
      const jobsQuery = query(
        collection(db, 'problems'),
        where('helperId', '==', helperId)
      );
      const jobsSnap = await getDocs(jobsQuery);
      allJobs = jobsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      logger.log(`📋 Found ${allJobs.length} jobs for helper ${helperId}`);
    } catch (queryError) {
      // If permission denied, return empty reputation (user can't see helper's jobs)
      if (queryError.code === 'permission-denied' || queryError.code === 'permissions-denied') {
        logger.log('Permission denied for reputation calculation - returning empty data');
        return {
          completionRate: 0,
          completionRateDisplay: 'No data available',
          completionDetails: 'No tasks yet',
          avgResponseTime: null,
          avgResponseTimeDisplay: null,
          jobsCompletedInLast7Days: 0,
          recentActivityDisplay: null,
          totalJobs: 0,
          completedJobs: 0,
        };
      }
      throw queryError; // Re-throw if it's a different error
    }

    // 1. Completion Rate
    const totalJobs = allJobs.length;
    const completedJobs = allJobs.filter(job => job.status === 'completed').length;
    const completionRate = totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0;

    // 2. Average Response Time
    let totalResponseTime = 0;
    let responseTimeCount = 0;

    // Get first message time for each job
    for (const job of allJobs) {
      if (job.assignedAt && job.status !== 'open') {
        try {
          // Get first message in the messages subcollection (non-system messages)
          const messagesQuery = query(
            collection(db, 'problems', job.id, 'messages'),
            orderBy('createdAt', 'asc')
          );
          const messagesSnap = await getDocs(messagesQuery);
          
          // Find first non-system message
          const firstUserMessage = messagesSnap.docs.find(doc => doc.data().type !== 'system');
          
          if (firstUserMessage) {
            const firstMessage = firstUserMessage.data();
            const firstMessageTime = firstMessage.createdAt;
            const assignedTime = job.assignedAt;

            if (firstMessageTime && assignedTime) {
              // Convert Firestore timestamps to milliseconds
              const firstMessageMs = firstMessageTime.toMillis 
                ? firstMessageTime.toMillis()
                : (firstMessageTime.seconds * 1000);
              const assignedMs = assignedTime.toMillis 
                ? assignedTime.toMillis()
                : (assignedTime.seconds * 1000);

              const responseTime = firstMessageMs - assignedMs;
              if (responseTime > 0) {
                totalResponseTime += responseTime;
                responseTimeCount++;
              }
            }
          }
        } catch (error) {
          // Skip jobs where we can't get messages
          // Permission errors are expected when clients view helper profiles (they can't read other clients' messages)
          // Only log non-permission errors to avoid console spam
          if (error.code !== 'permission-denied' && error.code !== 'permissions-denied') {
            console.warn(`Could not get response time for job ${job.id}:`, error.message);
          }
          // Silently continue for permission errors - this is normal behavior
        }
      }
    }

    const avgResponseTimeMs = responseTimeCount > 0 
      ? totalResponseTime / responseTimeCount 
      : null;
    
    // Format response time
    let avgResponseTimeDisplay = null;
    if (avgResponseTimeMs) {
      const minutes = Math.round(avgResponseTimeMs / (1000 * 60));
      if (minutes < 1) {
        avgResponseTimeDisplay = { value: '<1', unit: 'min', full: 'Replies in under 1 minute' };
      } else if (minutes < 10) {
        avgResponseTimeDisplay = { value: minutes, unit: 'min', full: `Replies in ${minutes} min` };
      } else if (minutes < 60) {
        avgResponseTimeDisplay = { value: minutes, unit: 'min', full: `Replies in ${minutes} minutes` };
      } else {
        const hours = Math.round(minutes / 60);
        avgResponseTimeDisplay = { value: hours, unit: 'hour', full: `Replies in ${hours} ${hours === 1 ? 'hour' : 'hours'}` };
      }
    }

    // 3. Recent Activity (jobs completed in last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const sevenDaysAgoMs = sevenDaysAgo.getTime();

    const jobsCompletedInLast7Days = allJobs.filter(job => {
      if (job.status !== 'completed' || !job.completedAt) return false;
      
      const completedMs = job.completedAt.toMillis 
        ? job.completedAt.toMillis()
        : (job.completedAt.seconds * 1000);
      
      return completedMs >= sevenDaysAgoMs;
    }).length;

    return {
      completionRate: Math.round(completionRate),
      completionRateDisplay: totalJobs > 0 
        ? `${Math.round(completionRate)}% successful tasks`
        : 'No jobs yet',
      completionDetails: totalJobs > 0
        ? `${completedJobs} of ${totalJobs} tasks completed`
        : 'No tasks yet',
      avgResponseTime: avgResponseTimeMs,
      avgResponseTimeDisplay: avgResponseTimeDisplay,
      jobsCompletedInLast7Days,
      recentActivityDisplay: jobsCompletedInLast7Days > 0
        ? `Completed ${jobsCompletedInLast7Days} ${jobsCompletedInLast7Days === 1 ? 'job' : 'jobs'} this week`
        : null,
      totalJobs,
      completedJobs,
    };
  } catch (error) {
    console.error('Error calculating helper reputation:', error);
    return {
      completionRate: 0,
      completionRateDisplay: 'No data',
      completionDetails: 'No tasks yet',
      avgResponseTime: null,
      avgResponseTimeDisplay: null,
      jobsCompletedInLast7Days: 0,
      recentActivityDisplay: null,
      totalJobs: 0,
      completedJobs: 0,
    };
  }
}

/**
 * Get reputation metrics for multiple helpers (optimized batch)
 * @param {Array<string>} helperIds - Array of helper user IDs
 * @returns {Promise<Object>} Map of helperId -> reputation metrics
 */
export async function calculateReputationForHelpers(helperIds) {
  const reputationMap = {};
  
  // Calculate reputation for each helper
  await Promise.all(
    helperIds.map(async (helperId) => {
      reputationMap[helperId] = await calculateHelperReputation(helperId);
    })
  );
  
  return reputationMap;
}

